import React from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { LayoutDashboard, Package, FolderTree, Wrench, FileText, Newspaper, Inbox, Users, Download, Settings as SettingsIcon, LogOut, Globe } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

const AdminLayout = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();

  const links = [
    { to: "/admin", label: t("admin.overview"), icon: LayoutDashboard, end: true },
    { to: "/admin/products", label: t("admin.products"), icon: Package },
    { to: "/admin/categories", label: t("admin.categories"), icon: FolderTree },
    { to: "/admin/services", label: t("admin.services"), icon: Wrench },
    { to: "/admin/catalogues", label: t("admin.catalogues"), icon: FileText },
    { to: "/admin/news", label: t("admin.news"), icon: Newspaper },
    { to: "/admin/inquiries", label: t("admin.inquiries"), icon: Inbox },
    { to: "/admin/catalogue-requests", label: t("admin.catalogueRequests"), icon: Download },
    { to: "/admin/team", label: t("admin.team"), icon: Users },
    { to: "/admin/settings", label: t("admin.settings"), icon: SettingsIcon },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-layout">
      <div className="grid grid-cols-12 min-h-screen">
        {/* Sidebar */}
        <aside className="col-span-12 md:col-span-3 lg:col-span-2 bg-[#0A1128] text-white p-6 md:min-h-screen">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center rounded-sm">
              <span className="font-display text-[#D4AF37]">N</span>
            </div>
            <div>
              <div className="font-display text-base tracking-[0.25em]">NAKIA</div>
              <div className="text-[9px] text-[#D4AF37]/70 tracking-[0.3em] mt-0.5">ADMIN</div>
            </div>
          </Link>

          <nav className="flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`admin-nav-${l.to.split("/").pop() || "dashboard"}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-sm text-xs uppercase tracking-[0.15em] font-medium transition-colors ${
                    isActive ? "bg-[#D4AF37] text-[#0A1128]" : "text-white/80 hover:bg-white/5 hover:text-[#D4AF37]"
                  }`
                }
              >
                <l.icon size={16} /> {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto pt-10 border-t border-white/10 mt-10">
            <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37] mb-4">
              <Globe size={14} /> View Site
            </Link>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs text-white/60 hover:text-[#D4AF37]">
              <LogOut size={14} /> {t("admin.logout")}
            </button>
            <div className="mt-5 text-[10px] text-white/40 truncate">{user?.email}</div>
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 md:col-span-9 lg:col-span-10 p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
